// DEV es la única fuente de verdad de parámetros/OT/Personalización Z —
// QAS y PRD heredan esos valores en modo lectura y solo registran la
// confirmación de transporte de la OT (ver TransportConfirmation).
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveGeneralsConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, GeneralsFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

// El checklist de la pestaña "ERP (Add-on)" depende del tipo de ERP del
// proyecto (ver erpTipo en erpConfig.ts): ECC / S/4HANA On-Premise usan el
// checklist clásico basado en RFC + Cloud Connector; S/4HANA Private/Public
// Cloud Edition no tiene RFC ni STRUST — se integra vía Communication
// Arrangement directo a CIG, así que usa un checklist distinto.
export const esErpCloud = (erpTipo?: string): boolean =>
  erpTipo === 'S4HANA_PRIVATE_CLOUD' || erpTipo === 'S4HANA_PUBLIC_CLOUD';

const TAB_STEPS_ERP_ONPREM = ['certificates', 'rfc', 'port', 'logicalsystem', 'activation'];

// Agrupación estricta de pasos por plataforma. `erp` se resuelve según el
// tipo de ERP del proyecto — ver getTabSteps. Sub-fase K8: en Cloud, el tab
// "erp" ya NO tiene pasos propios acá — el wizard viejo de 6 pasos
// (Usuario de Comunicación/System ID/Communication Arrangement/CIG URL/
// Scenario/Test) fue reemplazado por "Acceso al Tenant" + el pool de
// Communication Users/Systems, que viven en el Repositorio de Comunicación
// (K5/K4). Los datos viejos NUNCA se borraron — `loadEnvironmentData` más
// abajo los sigue leyendo sin problema, solo dejaron de tener UI propia acá.
export const TAB_STEPS: Record<string, string[]> = {
  erp: TAB_STEPS_ERP_ONPREM,
  cloudconnector: ['cc_subaccount', 'cc_virtual_host', 'cc_resources', 'cc_tunnel'],
  ariba: ['ap_site_config', 'ap_channels', 'ap_testing'],
  gateway: ['mg_project', 'mg_connections', 'mg_global_mapping'],
  sr: []
};

export const getTabSteps = (erpTipo?: string): Record<string, string[]> => ({
  ...TAB_STEPS,
  erp: esErpCloud(erpTipo) ? [] : TAB_STEPS_ERP_ONPREM
});

export const TAB_LABELS: Record<string, string> = {
  erp: 'ERP (Add-on)',
  cloudconnector: 'Cloud Connector',
  ariba: 'Ariba Portal',
  gateway: 'Managed Gateway (CIG)',
  sr: 'SR (Solicitudes)'
};

// Sub-fase K8: en Cloud no existe la versión "Add-on" (eso es un concepto
// on-premise) — la pestaña se relabela a "Acceso al Tenant", que es lo que
// efectivamente muestra (ver configuracionGeneral.tsx).
export const tabLabel = (tab: string, esCloudErp: boolean): string =>
  tab === 'erp' && esCloudErp ? 'Acceso al Tenant' : TAB_LABELS[tab];

// Mapeo id de paso -> clave del step_* en Firestore (ver saveGeneralsConfig).
const STEP_KEY_MAP: Record<string, string> = {
  certificates: 'step_certificates', rfc: 'step_rfc', port: 'step_port',
  logicalsystem: 'step_LogicalSystem', activation: 'step_activation',
  cloud_comm_user: 'step_cloud_comm_user', cloud_system_id: 'step_cloud_system_id',
  cloud_comm_arrangement: 'step_cloud_comm_arrangement', cloud_cig_url: 'step_cloud_cig_url',
  cloud_comm_scenario: 'step_cloud_comm_scenario', cloud_test_connection: 'step_cloud_test_connection',
  cc_subaccount: 'step_cc_subaccount', cc_virtual_host: 'step_cc_virtual_host',
  cc_resources: 'step_cc_resources', cc_tunnel: 'step_cc_tunnel',
  ap_site_config: 'step_ap_site_config', ap_channels: 'step_ap_channels', ap_testing: 'step_ap_testing',
  mg_project: 'step_mg_project', mg_connections: 'step_mg_connections', mg_global_mapping: 'step_mg_global_mapping'
};

const DEFAULT_FORM: GeneralsFormData = {
  // ERP on-premise
  rfcDestination: '', connectionType: 'G', realmId: '', apiClientId: '', dataCenter: 'usa',
  // ERP cloud
  cloudCommUser: '', cloudCommUserEnabled: false, cloudSystemId: '',
  cloudCommArrangementId: '', cloudCommScenarioId: '', cloudCigUrl: '',
  cloudApiActivated: false, cloudTestStatus: 'Disconnected',
  // Cloud Connector
  ccBtpRegion: 'EU', ccLocationId: '', ccDisplayName: '',
  ccSelectedSubaccountId: '', ccCompletedSubaccounts: [],
  ccMasterHost: '', ccVirtualHost: '', ccMasterPort: '', ccVirtualPort: '', ccProtocol: 'HTTPS',
  ccResourcePath: '', ccResourceEnabled: true, ccTunnelSubaccount: '', ccTunnelStatus: 'Disconnected',
  // Ariba Portal
  apRealmId: '', apIntegrationDir: '', apEnablePO: true, apEnableASN: true, apEnableInvoice: false, apEnableReceipt: false, apTestTargetUrl: '',
  // Managed Gateway (CIG)
  mgProjectId: '', mgSystemId: '', mgEndpointUrl: '', mgAuthType: 'Basic', mgOrgId: '', mgCompanyCode: '',
  // Comunes
  requiresOT: false, otNumber: '', comments: ''
};

export function useGeneralConfig() {
  const { projectId, project } = useProject();
  const tabSteps = getTabSteps(project?.erpTipo);
  const [activeTab, setActiveTab] = useState<'erp' | 'cloudconnector' | 'ariba' | 'gateway' | 'sr'>('erp');
  const [activeStep, setActiveStep] = useState('certificates');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  // Estado maestro del ambiente activo (Por defecto DEV)
  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  // Copia de seguridad de toda la data de Firestore
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<GeneralsFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleFieldChange = (field: keyof GeneralsFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // Al cambiar de tab, redefinimos el step activo al primero de ese tab
  useEffect(() => {
    const steps = tabSteps[activeTab];
    if (steps && steps.length > 0) {
      setActiveStep(steps[0]);
    }
    setValidationError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Siempre carga desde DEV — QAS/PRD heredan estos mismos valores en modo lectura.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    // Cargar progreso del checklist para el TAB activo actual
    if (devData.checklistProgress) {
      const allowedSteps = tabSteps[activeTab] || [];
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && allowedSteps.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<GeneralsFormData> = {};
    if (devData.dataCenter) initialFormData.dataCenter = devData.dataCenter;

    const generals = devData.channels?.generals;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.generals : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (generals) {
      // ERP Steps
      if (generals.step_rfc) {
        if (activeStep === 'rfc') {
          const requiresOT = generals.step_rfc.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_rfc : (envChannelData?.step_rfc || {});
          initialFormData.rfcDestination = source.rfcDestination || '';
          initialFormData.connectionType = source.connectionType || 'G';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, rfc: generals.step_rfc.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, rfc: generals.step_rfc.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, rfc: generals.step_rfc.customDevelopment || emptyCustomDevelopment() }));
        qasMap.rfc = generals.step_rfc.transport?.QAS || emptyTransportConfirmation();
        prdMap.rfc = generals.step_rfc.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_LogicalSystem) {
        if (activeStep === 'logicalsystem') {
          const requiresOT = generals.step_LogicalSystem.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_LogicalSystem : (envChannelData?.step_LogicalSystem || {});
          initialFormData.realmId = source.realmId || '';
          initialFormData.apiClientId = source.apiClientId || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, logicalsystem: generals.step_LogicalSystem.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, logicalsystem: generals.step_LogicalSystem.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, logicalsystem: generals.step_LogicalSystem.customDevelopment || emptyCustomDevelopment() }));
        qasMap.logicalsystem = generals.step_LogicalSystem.transport?.QAS || emptyTransportConfirmation();
        prdMap.logicalsystem = generals.step_LogicalSystem.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_certificates) {
        if (activeStep === 'certificates') {
          const requiresOT = generals.step_certificates.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_certificates : (envChannelData?.step_certificates || {});
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, certificates: generals.step_certificates.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, certificates: generals.step_certificates.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, certificates: generals.step_certificates.customDevelopment || emptyCustomDevelopment() }));
        qasMap.certificates = generals.step_certificates.transport?.QAS || emptyTransportConfirmation();
        prdMap.certificates = generals.step_certificates.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_activation) {
        if (activeStep === 'activation') {
          const requiresOT = generals.step_activation.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_activation : (envChannelData?.step_activation || {});
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, activation: generals.step_activation.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, activation: generals.step_activation.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, activation: generals.step_activation.customDevelopment || emptyCustomDevelopment() }));
        qasMap.activation = generals.step_activation.transport?.QAS || emptyTransportConfirmation();
        prdMap.activation = generals.step_activation.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_port) {
        qasMap.port = generals.step_port.transport?.QAS || emptyTransportConfirmation();
        prdMap.port = generals.step_port.transport?.PRD || emptyTransportConfirmation();
      }

      // ERP Cloud Steps (S/4HANA Private/Public Cloud Edition)
      if (generals.step_cloud_comm_user) {
        if (activeStep === 'cloud_comm_user') {
          const requiresOT = generals.step_cloud_comm_user.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cloud_comm_user : (envChannelData?.step_cloud_comm_user || {});
          initialFormData.cloudCommUser = source.cloudCommUser || '';
          initialFormData.cloudCommUserEnabled = source.cloudCommUserEnabled ?? false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cloud_comm_user: generals.step_cloud_comm_user.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cloud_comm_user: generals.step_cloud_comm_user.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cloud_comm_user: generals.step_cloud_comm_user.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cloud_comm_user = generals.step_cloud_comm_user.transport?.QAS || emptyTransportConfirmation();
        prdMap.cloud_comm_user = generals.step_cloud_comm_user.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cloud_system_id) {
        if (activeStep === 'cloud_system_id') {
          const requiresOT = generals.step_cloud_system_id.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cloud_system_id : (envChannelData?.step_cloud_system_id || {});
          initialFormData.cloudSystemId = source.cloudSystemId || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cloud_system_id: generals.step_cloud_system_id.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cloud_system_id: generals.step_cloud_system_id.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cloud_system_id: generals.step_cloud_system_id.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cloud_system_id = generals.step_cloud_system_id.transport?.QAS || emptyTransportConfirmation();
        prdMap.cloud_system_id = generals.step_cloud_system_id.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cloud_comm_arrangement) {
        if (activeStep === 'cloud_comm_arrangement') {
          const requiresOT = generals.step_cloud_comm_arrangement.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cloud_comm_arrangement : (envChannelData?.step_cloud_comm_arrangement || {});
          initialFormData.cloudCommArrangementId = source.cloudCommArrangementId || '';
          initialFormData.cloudCommScenarioId = source.cloudCommScenarioId || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cloud_comm_arrangement: generals.step_cloud_comm_arrangement.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cloud_comm_arrangement: generals.step_cloud_comm_arrangement.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cloud_comm_arrangement: generals.step_cloud_comm_arrangement.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cloud_comm_arrangement = generals.step_cloud_comm_arrangement.transport?.QAS || emptyTransportConfirmation();
        prdMap.cloud_comm_arrangement = generals.step_cloud_comm_arrangement.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cloud_cig_url) {
        if (activeStep === 'cloud_cig_url') {
          const requiresOT = generals.step_cloud_cig_url.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cloud_cig_url : (envChannelData?.step_cloud_cig_url || {});
          initialFormData.cloudCigUrl = source.cloudCigUrl || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cloud_cig_url: generals.step_cloud_cig_url.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cloud_cig_url: generals.step_cloud_cig_url.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cloud_cig_url: generals.step_cloud_cig_url.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cloud_cig_url = generals.step_cloud_cig_url.transport?.QAS || emptyTransportConfirmation();
        prdMap.cloud_cig_url = generals.step_cloud_cig_url.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cloud_comm_scenario) {
        if (activeStep === 'cloud_comm_scenario') {
          const requiresOT = generals.step_cloud_comm_scenario.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cloud_comm_scenario : (envChannelData?.step_cloud_comm_scenario || {});
          initialFormData.cloudApiActivated = source.cloudApiActivated ?? false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cloud_comm_scenario: generals.step_cloud_comm_scenario.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cloud_comm_scenario: generals.step_cloud_comm_scenario.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cloud_comm_scenario: generals.step_cloud_comm_scenario.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cloud_comm_scenario = generals.step_cloud_comm_scenario.transport?.QAS || emptyTransportConfirmation();
        prdMap.cloud_comm_scenario = generals.step_cloud_comm_scenario.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cloud_test_connection) {
        if (activeStep === 'cloud_test_connection') {
          const requiresOT = generals.step_cloud_test_connection.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cloud_test_connection : (envChannelData?.step_cloud_test_connection || {});
          initialFormData.cloudTestStatus = source.cloudTestStatus || 'Disconnected';
          currentStepComments = source.comments || '';
        }
        qasMap.cloud_test_connection = generals.step_cloud_test_connection.transport?.QAS || emptyTransportConfirmation();
        prdMap.cloud_test_connection = generals.step_cloud_test_connection.transport?.PRD || emptyTransportConfirmation();
      }

      // Cloud Connector Steps
      if (generals.step_cc_subaccount) {
        if (activeStep === 'cc_subaccount') {
          const requiresOT = generals.step_cc_subaccount.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cc_subaccount : (envChannelData?.step_cc_subaccount || {});
          initialFormData.ccBtpRegion = source.ccBtpRegion || 'EU';
          initialFormData.ccLocationId = source.ccLocationId || '';
          initialFormData.ccDisplayName = source.ccDisplayName || '';
          initialFormData.ccSelectedSubaccountId = source.ccSelectedSubaccountId || '';
          initialFormData.ccCompletedSubaccounts = source.ccCompletedSubaccounts || [];
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cc_subaccount: generals.step_cc_subaccount.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cc_subaccount: generals.step_cc_subaccount.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cc_subaccount: generals.step_cc_subaccount.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cc_subaccount = generals.step_cc_subaccount.transport?.QAS || emptyTransportConfirmation();
        prdMap.cc_subaccount = generals.step_cc_subaccount.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cc_virtual_host) {
        if (activeStep === 'cc_virtual_host') {
          const requiresOT = generals.step_cc_virtual_host.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cc_virtual_host : (envChannelData?.step_cc_virtual_host || {});
          initialFormData.ccMasterHost = source.ccMasterHost || '';
          initialFormData.ccVirtualHost = source.ccVirtualHost || '';
          initialFormData.ccMasterPort = source.ccMasterPort || '';
          initialFormData.ccVirtualPort = source.ccVirtualPort || '';
          initialFormData.ccProtocol = source.ccProtocol || 'HTTPS';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cc_virtual_host: generals.step_cc_virtual_host.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cc_virtual_host: generals.step_cc_virtual_host.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cc_virtual_host: generals.step_cc_virtual_host.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cc_virtual_host = generals.step_cc_virtual_host.transport?.QAS || emptyTransportConfirmation();
        prdMap.cc_virtual_host = generals.step_cc_virtual_host.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cc_resources) {
        if (activeStep === 'cc_resources') {
          const requiresOT = generals.step_cc_resources.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cc_resources : (envChannelData?.step_cc_resources || {});
          initialFormData.ccResourcePath = source.ccResourcePath || '';
          initialFormData.ccResourceEnabled = source.ccResourceEnabled ?? true;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cc_resources: generals.step_cc_resources.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cc_resources: generals.step_cc_resources.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cc_resources: generals.step_cc_resources.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cc_resources = generals.step_cc_resources.transport?.QAS || emptyTransportConfirmation();
        prdMap.cc_resources = generals.step_cc_resources.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_cc_tunnel) {
        if (activeStep === 'cc_tunnel') {
          const requiresOT = generals.step_cc_tunnel.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_cc_tunnel : (envChannelData?.step_cc_tunnel || {});
          initialFormData.ccTunnelSubaccount = source.ccTunnelSubaccount || '';
          initialFormData.ccTunnelStatus = source.ccTunnelStatus || 'Disconnected';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, cc_tunnel: generals.step_cc_tunnel.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, cc_tunnel: generals.step_cc_tunnel.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, cc_tunnel: generals.step_cc_tunnel.customDevelopment || emptyCustomDevelopment() }));
        qasMap.cc_tunnel = generals.step_cc_tunnel.transport?.QAS || emptyTransportConfirmation();
        prdMap.cc_tunnel = generals.step_cc_tunnel.transport?.PRD || emptyTransportConfirmation();
      }

      // Ariba Portal Steps
      if (generals.step_ap_site_config) {
        if (activeStep === 'ap_site_config') {
          const requiresOT = generals.step_ap_site_config.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_ap_site_config : (envChannelData?.step_ap_site_config || {});
          initialFormData.apRealmId = source.apRealmId || '';
          initialFormData.apIntegrationDir = source.apIntegrationDir || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, ap_site_config: generals.step_ap_site_config.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, ap_site_config: generals.step_ap_site_config.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, ap_site_config: generals.step_ap_site_config.customDevelopment || emptyCustomDevelopment() }));
        qasMap.ap_site_config = generals.step_ap_site_config.transport?.QAS || emptyTransportConfirmation();
        prdMap.ap_site_config = generals.step_ap_site_config.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_ap_channels) {
        if (activeStep === 'ap_channels') {
          const requiresOT = generals.step_ap_channels.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_ap_channels : (envChannelData?.step_ap_channels || {});
          initialFormData.apEnablePO = source.apEnablePO ?? true;
          initialFormData.apEnableASN = source.apEnableASN ?? true;
          initialFormData.apEnableInvoice = source.apEnableInvoice ?? false;
          initialFormData.apEnableReceipt = source.apEnableReceipt ?? false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, ap_channels: generals.step_ap_channels.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, ap_channels: generals.step_ap_channels.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, ap_channels: generals.step_ap_channels.customDevelopment || emptyCustomDevelopment() }));
        qasMap.ap_channels = generals.step_ap_channels.transport?.QAS || emptyTransportConfirmation();
        prdMap.ap_channels = generals.step_ap_channels.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_ap_testing) {
        if (activeStep === 'ap_testing') {
          const requiresOT = generals.step_ap_testing.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_ap_testing : (envChannelData?.step_ap_testing || {});
          initialFormData.apTestTargetUrl = source.apTestTargetUrl || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, ap_testing: generals.step_ap_testing.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, ap_testing: generals.step_ap_testing.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, ap_testing: generals.step_ap_testing.customDevelopment || emptyCustomDevelopment() }));
        qasMap.ap_testing = generals.step_ap_testing.transport?.QAS || emptyTransportConfirmation();
        prdMap.ap_testing = generals.step_ap_testing.transport?.PRD || emptyTransportConfirmation();
      }

      // Managed Gateway Steps
      if (generals.step_mg_project) {
        if (activeStep === 'mg_project') {
          const requiresOT = generals.step_mg_project.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_mg_project : (envChannelData?.step_mg_project || {});
          initialFormData.mgProjectId = source.mgProjectId || '';
          initialFormData.mgSystemId = source.mgSystemId || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, mg_project: generals.step_mg_project.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, mg_project: generals.step_mg_project.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, mg_project: generals.step_mg_project.customDevelopment || emptyCustomDevelopment() }));
        qasMap.mg_project = generals.step_mg_project.transport?.QAS || emptyTransportConfirmation();
        prdMap.mg_project = generals.step_mg_project.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_mg_connections) {
        if (activeStep === 'mg_connections') {
          const requiresOT = generals.step_mg_connections.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_mg_connections : (envChannelData?.step_mg_connections || {});
          initialFormData.mgEndpointUrl = source.mgEndpointUrl || '';
          initialFormData.mgAuthType = source.mgAuthType || 'Basic';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, mg_connections: generals.step_mg_connections.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, mg_connections: generals.step_mg_connections.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, mg_connections: generals.step_mg_connections.customDevelopment || emptyCustomDevelopment() }));
        qasMap.mg_connections = generals.step_mg_connections.transport?.QAS || emptyTransportConfirmation();
        prdMap.mg_connections = generals.step_mg_connections.transport?.PRD || emptyTransportConfirmation();
      }
      if (generals.step_mg_global_mapping) {
        if (activeStep === 'mg_global_mapping') {
          const requiresOT = generals.step_mg_global_mapping.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? generals.step_mg_global_mapping : (envChannelData?.step_mg_global_mapping || {});
          initialFormData.mgOrgId = source.mgOrgId || '';
          initialFormData.mgCompanyCode = source.mgCompanyCode || '';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, mg_global_mapping: generals.step_mg_global_mapping.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, mg_global_mapping: generals.step_mg_global_mapping.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, mg_global_mapping: generals.step_mg_global_mapping.customDevelopment || emptyCustomDevelopment() }));
        qasMap.mg_global_mapping = generals.step_mg_global_mapping.transport?.QAS || emptyTransportConfirmation();
        prdMap.mg_global_mapping = generals.step_mg_global_mapping.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.generals;
    const prdChannelData = configSource?.environments?.PRD?.channels?.generals;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    Object.keys(STEP_KEY_MAP).forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  // Escuchar cambios de step, tab o ambiente
  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, activeTab, selectedEnv]);

  // Carga inicial desde Firestore
  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleTestConnection = () => {
    setTestingConnection(true);
    setConnectionStatus('idle');
    setTimeout(() => { setTestingConnection(false); setConnectionStatus('success'); }, 1500);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    // Validaciones por paso
    if (activeStep === 'rfc' && !formData.rfcDestination.trim()) {
      setValidationError("El campo 'RFC Destination' es obligatorio."); return;
    }
    if (activeStep === 'cloud_comm_user' && !formData.cloudCommUser.trim()) {
      setValidationError("El ID del Usuario de Comunicación es obligatorio."); return;
    }
    if (activeStep === 'cloud_system_id' && !formData.cloudSystemId.trim()) {
      setValidationError("El System ID es obligatorio."); return;
    }
    if (activeStep === 'cloud_comm_arrangement' && !formData.cloudCommArrangementId.trim()) {
      setValidationError("El ID del Acuerdo de Comunicación es obligatorio."); return;
    }
    if (activeStep === 'cloud_cig_url' && !formData.cloudCigUrl.trim()) {
      setValidationError("La URL de CIG es obligatoria."); return;
    }
    if (activeStep === 'cc_subaccount' && (!formData.ccLocationId.trim() || !formData.ccDisplayName.trim())) {
      setValidationError("El Location ID y el Display Name son obligatorios."); return;
    }
    if (activeStep === 'cc_virtual_host' && (!formData.ccMasterHost.trim() || !formData.ccVirtualHost.trim())) {
      setValidationError("Los campos de Host y Puerto son obligatorios."); return;
    }
    if (activeStep === 'cc_tunnel' && !formData.ccTunnelSubaccount.trim()) {
      setValidationError("El ID de Subaccount es obligatorio."); return;
    }
    if (activeStep === 'ap_site_config' && (!formData.apRealmId.trim() || !formData.apIntegrationDir.trim())) {
      setValidationError("El Realm ID y el Integration Directory son obligatorios."); return;
    }
    if (activeStep === 'ap_testing' && !formData.apTestTargetUrl.trim()) {
      setValidationError("La URL de prueba es obligatoria."); return;
    }
    if (activeStep === 'mg_project' && (!formData.mgProjectId.trim() || !formData.mgSystemId.trim())) {
      setValidationError("El Project ID y System ID son obligatorios."); return;
    }
    if (activeStep === 'mg_connections' && !formData.mgEndpointUrl.trim()) {
      setValidationError("El Endpoint URL es obligatorio."); return;
    }
    if (activeStep === 'mg_global_mapping' && (!formData.mgOrgId.trim() || !formData.mgCompanyCode.trim())) {
      setValidationError("El Org ID y Company Code son obligatorios."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      // Se incluye el transport ya conocido para no perderlo: el guardado
      // de DEV reemplaza el objeto completo del paso en Firestore.
      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveGeneralsConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'generals', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      // Refrescar la copia local simulada para reflejar los cambios de inmediato
      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};

      // Actualizar progreso local
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'generals', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeTab,
    setActiveTab,
    activeStep,
    setActiveStep,
    completedSteps,
    formData,
    handleFieldChange,
    requiresOTMap,
    setRequiresOTMap,
    otNumberMap,
    setOtNumberMap,
    customDevMap,
    setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError,
    setValidationError,
    saveStatus,
    loadingData,
    testingConnection,
    connectionStatus,
    handleTestConnection,
    handleSave,
    selectedEnv,
    handleEnvChange,
    tabSteps,
    esCloudErp: esErpCloud(project?.erpTipo)
  };
}
